import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { settingsApi, getErrorMessage } from '../../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export const SEND_TYPE_OPTIONS = [
  { value: 'document', label: 'Document' },
  { value: 'packet', label: 'Packet' },
  { value: 'form', label: 'Form' },
];

/**
 * Settings -> Integrations -> Bullhorn ATS: which tab the Send dialog opens on when someone clicks
 * Send from a Bullhorn record. Saves as soon as a value is chosen.
 */
export default function DefaultBullhornSendType() {
  const [mode, setMode] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    settingsApi.getBullhornSendDefault()
      .then(({ data }) => { if (alive) setMode(data?.default_mode || 'document'); })
      .catch(() => { if (alive) setMode('document'); });
    return () => { alive = false; };
  }, []);

  const change = async (next) => {
    const previous = mode;
    setMode(next);
    setSaving(true);
    try {
      await settingsApi.setBullhornSendDefault(next);
      toast.success(`The Send dialog in Bullhorn will now open on ${SEND_TYPE_OPTIONS.find((o) => o.value === next)?.label}`);
    } catch (error) {
      setMode(previous);
      toast.error(getErrorMessage(error, 'Failed to save the default send type'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 p-4 bg-muted/30 rounded-lg" data-testid="default-bullhorn-send-type">
      <div>
        <h3 className="font-medium">Default Send Type</h3>
        <p className="text-sm text-muted-foreground">
          What the Send dialog opens on from a Bullhorn record. Whoever is sending can still switch tabs.
          If Forms isn't enabled for your workspace, it opens on Document.
        </p>
      </div>
      <Select value={mode ?? undefined} onValueChange={change} disabled={mode === null || saving}>
        <SelectTrigger className="w-40 flex-shrink-0" data-testid="default-bullhorn-send-type-select">
          <SelectValue placeholder="Loading…" />
        </SelectTrigger>
        <SelectContent>
          {SEND_TYPE_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
